import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Briefcase, ArrowRight, Sparkle, CurrencyInr } from "@phosphor-icons/react";
import api from "@/lib/api";
import Brand from "@/components/Brand";
import SeoMeta from "@/components/SeoMeta";
import { initPixels, pixelTrack } from "@/lib/pixels";

/**
 * Programmatic SEO landing — /jobs-in/{city}/{role}
 *
 * Aggregates every live role matching the (role, city) combo into a
 * stable, high-authority long-tail landing page. Ranked by Google for
 * queries like "react developer jobs in bengaluru", "product manager
 * mumbai" etc.
 *
 * Powered by /api/public/jobs-in/{city}/{role} on the backend, which
 * reuses the same city/role taxonomy that the sitemap builder consumes.
 */

function fmtSalary(j) {
    return j.salary_range || j.salary || "";
}

export default function ProgrammaticJobsLanding() {
    const { city, role } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        initPixels();
        pixelTrack("landing_view", { source: "programmatic_seo", city, role });
        let alive = true;
        setLoading(true);
        api.get(`/public/jobs-in/${city}/${role}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => { if (alive) setErr(e?.response?.status === 404 ? "not_found" : "error"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [city, role]);

    if (err === "not_found") {
        return (
            <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 text-center">
                <SeoMeta fallback={{ title: "Not found — NaukriDaddy", robots: "noindex, follow" }} />
                <div className="max-w-md">
                    <h1 className="font-heading text-3xl font-black mb-3">Page not found</h1>
                    <p className="text-[#525252] mb-6">This role/city combo doesn't exist yet.</p>
                    <Link to="/jobs" className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-white text-[11px] font-black uppercase tracking-wider">
                        Browse all open roles <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>
            </div>
        );
    }

    const roleLabel = data?.role?.label || "Jobs";
    const cityLabel = data?.city?.label || "India";
    const total = data?.total || 0;

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-ink" data-testid="programmatic-jobs-page">
            <SeoMeta endpoint={`/seo/jobs-in/${city}/${role}/meta`} />

            {/* Header — same visual as PublicJobsBoard for brand consistency */}
            <header className="bg-ink text-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                    <Link to="/" data-testid="pjl-logo-home"><Brand variant="light" size="h-7 sm:h-8" to={null} /></Link>
                    <div className="flex gap-2">
                        <Link to="/jobs" data-testid="pjl-header-all-jobs" className="text-[11px] font-black uppercase tracking-wider hover:text-cobalt">All jobs</Link>
                        <Link to="/resume-studio" data-testid="pjl-header-resume" className="text-[11px] font-black uppercase tracking-wider hover:text-cobalt">Resume Studio</Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="border-b border-[#E5E5E5] bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                    <p className="text-[11px] font-black uppercase tracking-[0.25em] text-cobalt mb-3">
                        {loading ? "· · ·" : `${total} open role${total === 1 ? "" : "s"}`}
                    </p>
                    <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-4">
                        {roleLabel} Jobs<br />
                        <span className="text-cobalt">in {cityLabel}</span>
                    </h1>
                    <p className="text-base sm:text-lg text-[#525252] max-w-2xl leading-relaxed mb-6">
                        Every live {roleLabel.toLowerCase()} opening from Indian hiring agencies in {cityLabel} — apply directly, no cold calls, no candidate fees. {data?.salary_hint ? <>Typical range: <strong className="text-ink">{data.salary_hint}</strong>.</> : null}
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            to="/resume-studio"
                            onClick={() => pixelTrack("rewrite_free_click", { source: "programmatic_seo_hero" })}
                            data-testid="pjl-cta-resume"
                            className="inline-flex items-center gap-2 px-5 py-3 bg-cobalt text-white text-[11px] font-black uppercase tracking-wider hover:opacity-90"
                        >
                            <Sparkle size={14} weight="fill" /> Free CV Score
                        </Link>
                        <Link
                            to="/jobs"
                            data-testid="pjl-cta-browse"
                            className="inline-flex items-center gap-2 px-5 py-3 border-2 border-ink text-[11px] font-black uppercase tracking-wider hover:bg-ink hover:text-white"
                        >
                            <Briefcase size={14} weight="bold" /> Browse all cities
                        </Link>
                    </div>
                </div>
            </section>

            {/* Job list */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                <h2 className="font-heading text-xl sm:text-2xl font-black tracking-tight mb-6">
                    Open {roleLabel.toLowerCase()} roles in {cityLabel}
                </h2>
                {loading && <p className="text-[#A3A3A3] text-sm">Loading…</p>}
                {!loading && data?.items?.length === 0 && (
                    <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                        <p className="text-[#525252] mb-4">
                            No live {roleLabel.toLowerCase()} openings in {cityLabel} today. New roles land almost daily — set an alert or browse all jobs.
                        </p>
                        <Link
                            to="/jobs"
                            data-testid="pjl-empty-browse"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white text-[11px] font-black uppercase tracking-wider"
                        >
                            Browse all open roles <ArrowRight size={12} weight="bold" />
                        </Link>
                    </div>
                )}
                <div className="space-y-3">
                    {(data?.items || []).map((j) => {
                        const href = j.client?.slug ? `/careers/${j.client.slug}/${j.id}` : `/jobs/${j.id}`;
                        return (
                            <Link
                                key={j.id}
                                to={href}
                                data-testid={`pjl-job-${j.id}`}
                                onClick={() => pixelTrack("job_card_click", { source: "programmatic_seo", job_id: j.id })}
                                className="block bg-white border border-[#E5E5E5] hover:border-ink p-5 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-black text-lg text-ink truncate">{j.title}</h3>
                                        <p className="text-sm text-[#525252] mt-1">
                                            {j.client?.name || "Confidential client"}
                                        </p>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] font-black uppercase tracking-wider text-[#525252]">
                                            {j.location && (
                                                <span className="inline-flex items-center gap-1"><MapPin size={11} weight="bold" /> {j.location}</span>
                                            )}
                                            {fmtSalary(j) && (
                                                <span className="inline-flex items-center gap-1 text-cobalt"><CurrencyInr size={11} weight="bold" /> {fmtSalary(j)}</span>
                                            )}
                                            {j.employment_type && (
                                                <span className="inline-flex items-center gap-1"><Briefcase size={11} weight="bold" /> {j.employment_type}</span>
                                            )}
                                        </div>
                                    </div>
                                    <ArrowRight size={20} weight="bold" className="mt-1 flex-shrink-0 text-[#A3A3A3]" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* SEO content block — long-tail keyword-rich footer for Google */}
            <section className="border-t border-[#E5E5E5] bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                    <h2 className="font-heading text-xl font-black tracking-tight mb-4">
                        Finding {roleLabel.toLowerCase()} jobs in {cityLabel}
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6 text-sm text-[#525252] leading-relaxed">
                        <div>
                            <h3 className="font-black text-ink mb-2 text-[13px] uppercase tracking-wider">Apply directly</h3>
                            <p>Every listing goes straight to the hiring agency's recruiter — no middle-agent, no data-broker resale. You control who sees your CV.</p>
                        </div>
                        <div>
                            <h3 className="font-black text-ink mb-2 text-[13px] uppercase tracking-wider">Free CV rewrite</h3>
                            <p>Before you apply, run your CV through <Link to="/resume-studio" className="text-cobalt border-b border-cobalt">Resume Studio</Link> — free ATS score, AI keyword suggestions, and a 60-second rewrite tuned to {cityLabel} recruiter expectations.</p>
                        </div>
                        <div>
                            <h3 className="font-black text-ink mb-2 text-[13px] uppercase tracking-wider">Zero candidate fees</h3>
                            <p>Recruiters pay NaukriDaddy — you never pay a rupee to us or any "consultant". This applies to every {cityLabel} role listed here.</p>
                        </div>
                    </div>

                    {/* Related cities — internal linking for SEO */}
                    <div className="mt-10 pt-8 border-t border-[#E5E5E5]">
                        <p className="text-[11px] font-black uppercase tracking-wider text-[#525252] mb-3">
                            {roleLabel} jobs in other cities:
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {["bengaluru", "mumbai", "delhi", "hyderabad", "pune", "chennai", "gurugram", "noida", "remote"]
                                .filter((c) => c !== city)
                                .map((c) => (
                                    <Link
                                        key={c}
                                        to={`/jobs-in/${c}/${role}`}
                                        data-testid={`pjl-related-city-${c}`}
                                        className="px-3 py-1.5 border border-[#E5E5E5] hover:border-cobalt hover:text-cobalt text-[11px] font-black uppercase tracking-wider capitalize"
                                    >
                                        {c.replace("-", " ")}
                                    </Link>
                                ))}
                        </div>
                    </div>
                </div>
            </section>

            <footer className="border-t border-[#E5E5E5] bg-[#FAFAFA]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-center text-[11px] text-[#A3A3A3]">
                    © NaukriDaddy · <Link to="/how-it-works" className="hover:text-ink">How it works</Link> · <Link to="/jobs" className="hover:text-ink">All jobs</Link> · <Link to="/resume-studio" className="hover:text-ink">Resume Studio</Link>
                </div>
            </footer>
        </div>
    );
}
